import { IAngularEvent, IPromise, IQService, IRootScopeService, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IIndicator, IUIError } from "@gtmhub/error-handling";
import { OkrEvents } from "@gtmhub/okrs/events";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { INgRedux } from "@gtmhub/state-management";
import { ITeamsIdMap, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { IAllowedUsersAndTeams, IUser, IUsersIdMap } from "@gtmhub/users/models";
import { UserService } from "@gtmhub/users/user-service";
import { ISessionsIdMap } from "@webapp/sessions/models/sessions.model";
import { IGoal } from "../models";
import { GoalService } from "../services/goal.service";

export interface ISendForApprovalParamsService {
  id: string;
}

export interface ISendForApprovalScope extends IScope, IOwnerSelectorRedux {
  indicators: {
    loading?: IIndicator;
    sending?: IIndicator;
    validating?: IIndicator;
  };
  usersIdMap: IUsersIdMap;
  goal: IGoal;
  usersSelection: {
    valid: string[];
    invalid: string[];
    fake: string[];
    excluded: string[];
  };
  allowedToApprove: IAllowedUsersAndTeams;
  removeUserFromSelection(userId: string): void;
  sendForApproval(): void;
}

interface IOwnerSelectorRedux {
  teamsIdMap: ITeamsIdMap;
  teamsFetched: boolean;
  allSessions: ISessionsIdMap;
  sessionsAreFetched: boolean;
  reduxError: IUIError;
}

const bindStateToScope = (state: ITeamsStoreState & ISessionsStoreState): IOwnerSelectorRedux => {
  return {
    teamsIdMap: state.teams.map,
    teamsFetched: state.teams.isFetched,
    allSessions: state.sessions.map,
    sessionsAreFetched: state.sessions.isFetched,
    reduxError: state.sessions.error || state.teams.error,
  };
};

export class SendForApprovalCtrl {
  public static $inject = ["$rootScope", "$scope", "$state", "$stateParams", "$q", "GoalService", "UserService", "$ngRedux", "TeamsActions", "PlanningSessionsActions"];
  private ownerEnum = { user: "user", team: "team" };

  private okrEvents: OkrEvents;

  constructor(
    private $rootScope: IRootScopeService,
    private $scope: ISendForApprovalScope,
    private $state: StateService,
    private $stateParams: ISendForApprovalParamsService,
    private $q: IQService,
    private goalService: GoalService,
    private userService: UserService,
    private $ngRedux: INgRedux,
    private teamsActions: TeamsActions,
    private planningSessionActions: PlanningSessionsActions
  ) {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.planningSessionActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

    this.$scope.$on("usersSelected", this.onUsersSelected);

    this.okrEvents = new OkrEvents(this.$scope);

    this.init();
  }

  private goalFields = "ownerIds,sessionId,workflow{status,reviewersNeeded,reviewers,approvedAt},private";

  private strings = {
    goal: "goal",
    usersIdMap: "usersIdMap",
    read: "read",
    update: "update",
  };

  private init = (): void => {
    this.$scope.indicators = {};

    this.$scope.usersSelection = {
      valid: [],
      invalid: [],
      fake: [],
      excluded: [],
    };

    this.$scope.removeUserFromSelection = this.removeUserFromSelection;
    this.$scope.sendForApproval = this.sendForApproval;

    const goalPromise: IPromise<IGoal> = this.goalService.getGoalWithFields(this.$stateParams.id, this.goalFields);
    const userIdMapPromise: IPromise<IUsersIdMap> = this.userService.getUsersIdMap();

    this.$scope.indicators.loading = { progress: true };

    this.$q
      .all({
        goal: goalPromise,
        usersIdMap: userIdMapPromise,
      })
      .then(
        (resolvedPromiseMap) => {
          delete this.$scope.indicators.loading;

          this.$scope.goal = resolvedPromiseMap[this.strings.goal];
          this.$scope.usersIdMap = resolvedPromiseMap[this.strings.usersIdMap];

          this.processGoalWorkflowData();

          this.processSelfApproveOfOkrs();

          this.processSessionApprovalList();
        },
        (error) => (this.$scope.indicators.loading = { error })
      );
  };

  private processSelfApproveOfOkrs = (): void => {
    if (this.isSelfApproveDisabled(this.$scope.goal.sessionId)) {
      this.$scope.goal.ownerIds.forEach((ownerId) => {
        const ownerType = this.getOwnerType(ownerId);

        if (ownerType === this.ownerEnum.team) {
          const team = this.$scope.teamsIdMap[ownerId];
          team.members?.forEach((teamMember) => {
            if (!this.isUserManagerOfTeam(teamMember, team.manager)) {
              this.$scope.usersSelection.excluded.push(teamMember);
            }
          });
        } else {
          this.$scope.usersSelection.excluded.push(ownerId);
        }
      });
    }
  };

  private isSelfApproveDisabled = (sessionId: string): boolean => {
    return !this.$scope.allSessions[sessionId].isSelfApproveAllowed;
  };

  private getOwnerType = (ownerId: string): string => {
    return this.$scope.teamsIdMap[ownerId] ? this.ownerEnum.team : this.ownerEnum.user;
  };

  private isUserManagerOfTeam = (userId: string, managerId: string): boolean => {
    return userId === managerId;
  };

  private processGoalWorkflowData = (): void => {
    if (this.$scope.goal.workflow && this.$scope.goal.workflow.reviewers) {
      this.$scope.usersSelection.valid = this.$scope.goal.workflow.reviewers.reduce((reviewersIdsArr, currentReviewer) => {
        reviewersIdsArr.push(currentReviewer.id);

        return reviewersIdsArr;
      }, []);
    }

    this.processFakeUsers();
  };

  private processFakeUsers = (): void => {
    let fakeUsersLength: number = this.$scope.goal.workflow.reviewersNeeded - this.$scope.usersSelection.valid.length;
    fakeUsersLength = fakeUsersLength >= 0 ? fakeUsersLength : 0;

    const fakeUsersIds: string[] = [];

    for (let i = 0; i < fakeUsersLength; i++) {
      fakeUsersIds.push("");
    }

    this.$scope.usersSelection.fake = fakeUsersIds;
  };

  private processSessionApprovalList = (): void => {
    const goalSession = this.$scope.allSessions[this.$scope.goal.sessionId];

    if (!goalSession.workflow) {
      return;
    }

    if (goalSession.workflow.approvalSettings.everyoneCanApprove) {
      return;
    }

    this.$scope.allowedToApprove = {
      allowedUserIds: goalSession.workflow.approvalSettings.usersAllowedToApprove,
      allowedTeamIds: goalSession.workflow.approvalSettings.teamsAllowedToApprove,
    };
  };

  private onUsersSelected = (ev: IAngularEvent, users: string[]): void => {
    this.checkSelectedUsersPermissions(users);
  };

  private hasPermissionManageGoals = (userId: string, users: IUser[]): boolean => {
    const user = users.find((user) => user.id === userId);
    const permission = user.permissions.includes("ManageGoals");

    return user && permission;
  };

  private hasPermissionAccessPrivateGoals = (userId: string, users: IUser[]): boolean => {
    const user = users.find((user) => user.id === userId);
    const permission = user.permissions.includes("AccessPrivateGoals");

    return user && permission;
  };

  private checkIfUserIsInvalidByPermissionManageGoals = (userId: string, users: IUser[]): void => {
    if (!this.$scope.usersSelection.invalid.includes(userId) && !this.hasPermissionManageGoals(userId, users)) {
      this.$scope.usersSelection.invalid.push(userId);
    }
  };

  private checkIfUserIsInvalidByPermissionAccessPrivateGoals = (userId: string, users: IUser[]): void => {
    if (!this.$scope.usersSelection.invalid.includes(userId) && !this.hasPermissionAccessPrivateGoals(userId, users) && this.$scope.goal.private) {
      this.$scope.usersSelection.invalid.push(userId);
    }
  };

  private checkSelectedUsersPermissions = (userIds: string[]): void => {
    const permissions: string[] = [this.strings.read, this.strings.update];

    this.$scope.indicators.validating = { progress: true };

    const checkUsersGoalPermissionsPromise = this.goalService.checkUsersGoalPermissions(this.$scope.goal.id, userIds, permissions) as unknown as { users: string[] };
    const getUsersV2Promise = this.userService.getUsersV2({ filter: { _id: { $in: userIds } }, fields: "id,permissions" });

    this.$q.all([checkUsersGoalPermissionsPromise, getUsersV2Promise]).then(
      ([invalidIds, selectedUsers]) => {
        delete this.$scope.indicators.validating;

        if (!invalidIds || !invalidIds.users) {
          this.$scope.usersSelection.invalid = [];
          this.$scope.usersSelection.valid = [];
          userIds.forEach((userId) => {
            this.checkIfUserIsInvalidByPermissionManageGoals(userId, selectedUsers.items);
            this.checkIfUserIsInvalidByPermissionAccessPrivateGoals(userId, selectedUsers.items);
            if (!this.$scope.usersSelection.invalid.includes(userId) && this.hasPermissionManageGoals(userId, selectedUsers.items)) {
              this.$scope.usersSelection.valid.push(userId);
            }
          });
        } else {
          this.$scope.usersSelection.invalid = invalidIds.users;

          const validUserIds: string[] = [];

          userIds.forEach((userId) => {
            this.checkIfUserIsInvalidByPermissionManageGoals(userId, selectedUsers.items);
            this.checkIfUserIsInvalidByPermissionAccessPrivateGoals(userId, selectedUsers.items);
            if (!this.$scope.usersSelection.invalid.includes(userId) && this.hasPermissionManageGoals(userId, selectedUsers.items)) {
              validUserIds.push(userId);
            }
          });

          this.$scope.usersSelection.valid = validUserIds;
        }

        this.processFakeUsers();
      },
      (error) => (this.$scope.indicators.validating = { error })
    );
  };

  private removeUserFromSelection = (userId: string): void => {
    this.$scope.usersSelection.valid = this.$scope.usersSelection.valid.reduce((newSelection, currentUserId) => {
      if (currentUserId !== userId) {
        newSelection.push(currentUserId);
      }

      return newSelection;
    }, []);

    this.processFakeUsers();
  };

  private sendForApproval = (): void => {
    this.$scope.indicators.sending = { progress: true };

    this.goalService.sendForApproval(this.$scope.goal.id, this.$scope.usersSelection.valid).then(
      () => {
        delete this.$scope.indicators.sending;

        this.okrEvents.broadcastGoalsChanged("goalChanged");
        this.$rootScope.$broadcast("goalsChanged");

        this.$state.go("^.^");
      },
      (error) => (this.$scope.indicators.sending = { error })
    );
  };
}
